import { useEffect, useState } from 'react';
import { Modal } from '@carbon/react';
import { TextInput } from '@mantine/core';
import { defineMessages, useIntl } from 'react-intl';

import sharedMessages from 'Components/utils/sharedMessages';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { EventRoleDto, useUpdateEventRoleMutation } from 'api/eventRoleEndpoints';

import styles from './EventRoleEditModal.module.scss';

interface Props {
    open: boolean;
    role: EventRoleDto | null;
    onClose: () => void;
}

const EventRoleEditModal = ({ open, role, onClose }: Props) => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const [currentName, setCurrentName] = useState('');
    const [updateRole, { isLoading }] = useUpdateEventRoleMutation();

    useEffect(() => {
        if (role?.name) {
            setCurrentName(role.name);
        }
    }, [role]);

    const onSave = async () => {
        if (!role || !currentEntityId) return;
        await updateRole({
            entityId: currentEntityId,
            id: role.id,
            data: { id: role.id, name: currentName.trim() },
        });
        onClose();
    };

    return (
        <Modal
            preventCloseOnClickOutside
            open={open}
            modalHeading={intl.formatMessage(messages.heading, { name: role?.name })}
            primaryButtonText={intl.formatMessage(sharedMessages.save)}
            secondaryButtonText={intl.formatMessage(sharedMessages.cancel)}
            onRequestClose={onClose}
            onRequestSubmit={onSave}
            loadingStatus={isLoading ? 'active' : undefined}
            primaryButtonDisabled={isLoading || currentName.trim().length < 3}
        >
            <div className={styles.container}>
                <TextInput
                    id="event-role-edit-name"
                    label={intl.formatMessage(messages.label)}
                    value={currentName}
                    onChange={(e) => setCurrentName(e.target.value)}
                    placeholder={intl.formatMessage(messages.placeholder)}
                    size="md"
                    type="text"
                />
            </div>
        </Modal>
    );
};

const messages = defineMessages({
    heading: {
        id: 'eventRoles.modal.heading',
        defaultMessage: 'Zmień nazwę posługi liturgicznej "{name}"',
    },
    label: {
        id: 'eventRoles.modal.label',
        defaultMessage: 'Nazwa roli',
    },
    placeholder: {
        id: 'eventRoles.modal.placeholder',
        defaultMessage: 'Wprowadź nazwę roli',
    },
});

export default EventRoleEditModal;
